import React from 'react';
import { motion } from 'framer-motion';
import { Eye, Heart, DollarSign, AlertTriangle, Users, Clock } from 'lucide-react';

const Problem = () => {
  const problems = [
    {
      icon: Eye,
      title: "Invisible Plaque Goes Undetected",
      description: "Traditional visual inspection misses up to 40% of plaque buildup, leading to undetected oral health issues that progress silently.",
      color: "text-red-500",
      bgColor: "bg-red-50",
      borderColor: "border-red-200"
    },
    {
      icon: Heart,
      title: "Gum Disease Affects 50%+ of Adults",
      description: "Periodontal disease is widespread but often asymptomatic until advanced stages, requiring costly and invasive treatment procedures.",
      color: "text-orange-500",
      bgColor: "bg-orange-50",
      borderColor: "border-orange-200"
    },
    {
      icon: DollarSign,
      title: "Dental Care is Expensive & Inaccessible",
      description: "Regular checkups cost $100-300+ and many people lack access to quality dental care, especially in underserved communities.",
      color: "text-blue-500",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200"
    }
  ];

  const statistics = [
    {
      number: "40%",
      label: "of plaque missed by visual inspection",
      icon: AlertTriangle,
      color: "text-red-600",
      bgColor: "bg-red-50"
    },
    {
      number: "50%+",
      label: "of adults over 30 have gum disease",
      icon: Users,
      color: "text-orange-600",
      bgColor: "bg-orange-50"
    },
    {
      number: "$100-300",
      label: "cost per dental checkup",
      icon: DollarSign,
      color: "text-blue-600",
      bgColor: "bg-blue-50"
    },
    {
      number: "6-12 months",
      label: "between recommended checkups",
      icon: Clock,
      color: "text-purple-600",
      bgColor: "bg-purple-50"
    }
  ];

  return (
    <section id="problem" className="section-padding bg-gradient-to-br from-gray-50 to-white">
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center px-4 py-2 bg-red-50 border border-red-200 rounded-full text-sm font-medium text-red-700 mb-6">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Critical Gap in Dental Care
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            The <span className="text-gradient">Problem</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Traditional dental care has critical gaps that leave millions vulnerable to preventable oral diseases. 
            Current methods are inadequate, expensive, and often inaccessible.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8 mb-20">
          {problems.map((problem, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
              className={`text-center p-8 rounded-2xl ${problem.bgColor} border ${problem.borderColor} hover:shadow-xl transition-all duration-300 card-hover`}
            >
              <div className={`w-20 h-20 ${problem.color} mx-auto mb-6 flex items-center justify-center bg-white rounded-2xl shadow-lg border ${problem.borderColor}`}>
                <problem.icon className="w-10 h-10" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {problem.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {problem.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Enhanced Statistics */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {statistics.map((stat, index) => (
            <div key={index} className={`p-6 ${stat.bgColor} rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300`}>
              <div className="flex items-center justify-center mb-4">
                <stat.icon className={`w-8 h-8 ${stat.color}`} />
              </div>
              <div className={`text-3xl font-bold ${stat.color} mb-2`}>{stat.number}</div>
              <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
            </div>
          ))}
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              There's a Better Way
            </h3>
            <p className="text-gray-600 mb-6">
              Oralytics combines advanced UV imaging technology with AI to provide accurate, 
              affordable, and accessible dental health monitoring.
            </p>
            <button 
              onClick={() => document.getElementById('solution').scrollIntoView({ behavior: 'smooth' })}
              className="btn-primary"
            >
              See Our Solution
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Problem; 